/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { KPIContext } from '@/context';
import { request } from '@/services/request';
import { MAX_FACT_IDS } from '@/utils/constants';
import { formatDateForMeasure } from '@/utils/formatDateForMeasure';
import { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import { KPI } from 'types/kpi-data';
import { ErrorGroup } from 'types/sankey';

import { RadioOption } from '@/components/combobox/enhanced-radio-select';

export const KPIProvider: React.FC<{
  children: ReactNode;
  workflowName?: string;
}> = ({ children, workflowName }) => {
  const searchParams = useSearchParams();
  const [data, setData] = useState<{ [key: string]: KPI }>();
  const [loading, setLoading] = useState(false);
  const [currentAppName, setCurrentAppName] = useState<string>();
  const [fetchRange, setFetchRange] = useState<DateRange>();
  const [selectedQualityFilter, setSelectedQualityFilter] =
    useState<RadioOption | null>(null);
  const [sankeyData, setSankeyData] = useState<{
    [key: string]: { label: string; value: number };
  }>();
  const [metaData, setMetaData] = useState<{
    [key: string]: { label: string; value: number };
  }>();

  const [sankeyDataLoading, setSankeyDataLoading] = useState<boolean>(false);
  const [sankeyDataRefreshTime, setSankeyDataRefreshTime] = useState<
    string | null
  >(null);
  const [factsData, setFactsData] = useState<{ key: string; label: string }[]>(
    []
  );
  const [factsLoading, setFactsLoading] = useState(false);
  const [nodeKey, setNodeKey] = useState<string>('');
  const [categorizedErrors, setCategorizedErrors] = useState<
    ErrorGroup[] | null
  >(null);
  const [responseError, setResponseError] = useState<any>(null);
  const [factName, setFactName] = useState<string>('');
  const [viewBy, setViewBy] = useState<string>('All');
  const [workflowNames, setWorkflowNames] = useState<string | null>(
    workflowName || searchParams.get('workflow_names')
  );
  const [selectedFactFilter, setSelectedFactFilter] = useState<string | null>(
    searchParams.get('filter_fact')
  );
  const [selectedFactIds, setSelectedFactIds] = useState<string | null>(
    searchParams.get('filter_fact_ids')
  );

  const fetchSankeyData = useCallback(
    async (
      appName: string,
      factName: string | null = null,
      subviewName: string | undefined = '',
      range?: any,
      workflows?: string | null,
      selectedFactFilter?: string | null,
      selectedFactIds?: string | null
    ) => {
      try {
        setResponseError(null);

        if (!factName) return;
        setSankeyDataLoading(true);
        setCurrentAppName(appName);
        // Use provided range or generate default

        const from = range?.from;
        const to = range?.to;

        const queryParams: Record<string, any> = {};
        // Apply time range filter
        if (from && to) {
          queryParams.start_time = from.toISOString();
          queryParams.end_time = to.toISOString();
        }
        // Apply workflow filter
        if (workflows) {
          queryParams.workflow_names = workflows;
        }
        // Apply fact filter
        if (selectedFactFilter) {
          queryParams.filter_fact = selectedFactFilter;
        }
        // Apply fact IDs filter
        if (selectedFactIds) {
          queryParams.filter_fact_ids = selectedFactIds;
        }
        // Apply eval metadata for quality view navigation
        queryParams.return_eval_filters = 'true';

        const endpoint = subviewName
          ? `insights/apps/${appName}/facts/${factName}/views/quality/subviews/${subviewName}`
          : `insights/apps/${appName}/facts/${factName}`;

        const fetchedData = await request.get({
          endpoint,
          ...(queryParams && { queryParams }),
        });

        setSankeyData(fetchedData[0]?.nodes);

        setFetchRange(range);
        const currentTimeStamp = formatDateForMeasure(new Date());
        setSankeyDataRefreshTime(`Last refreshed on ${currentTimeStamp}`);
      } catch (error) {
        console.error('Error fetching Sankey data:', error);
      } finally {
        setSankeyDataLoading(false);
      }
    },
    []
  );

  const fetchMetaData = useCallback(
    async (
      appName: string,
      factName: string | null = null,
      range?: DateRange
    ) => {
      try {
        const queryParams: Record<string, string> = {};

        if (range?.from && range?.to) {
          queryParams.start_time = range.from.toISOString();
          queryParams.end_time = range.to.toISOString();
        }

        const fetchingMetadataData = await request.get({
          endpoint: `metadata/apps/${appName}/facts/${factName}`,
          queryParams,
        });

        setMetaData(fetchingMetadataData[0]?.sankey_views);
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    },
    []
  );

  const fetchCategorizedErrors = useCallback(
    async (
      appName: string,
      breakdownFilter: string,
      range?: DateRange,
      clear = false,
      workflows?: string | null,
      filterFact?: string | null,
      filterFactIds?: string | null
    ) => {
      // If 'clear' is true, reset error categorization.
      if (clear) {
        setCategorizedErrors(null);
        return;
      }
      try {
        setCategorizedErrors(null);
        setResponseError(null);

        const queryParams: Record<string, string> = {};

        if (breakdownFilter) {
          queryParams.breakdown_filter = breakdownFilter;
        }

        if (range?.from && range?.to) {
          queryParams.start_time = range.from.toISOString();
          queryParams.end_time = range.to.toISOString();
        }

        if (workflows) {
          queryParams.workflow_names = workflows;
        }

        if (filterFact) {
          queryParams.filter_fact = filterFact;
        }

        if (filterFactIds) {
          queryParams.filter_fact_ids = filterFactIds;
        }

        const fetchedData = await request.get(
          {
            endpoint: `insights/apps/${appName}/error_groups`,
            queryParams,
          },
          false,
          true
        );

        setCategorizedErrors(fetchedData[0]?.error_groups || []);
      } catch (error) {
        setResponseError(error);
        console.error('Error fetching categorized errors:', error);
        setCategorizedErrors([]);
      }
    },
    [categorizedErrors]
  );

  const fetchKPI = useCallback(async (appName: string, range?: any) => {
    try {
      setResponseError(null);
      setLoading(true);
      setCurrentAppName(appName);

      // Use provided range or generate default

      const from = range?.from;
      const to = range?.to;

      const queryParams =
        !from && !to
          ? null
          : {
              start_time: from.toISOString(),
              end_time: to.toISOString(),
            };
      const endpoint = workflowName
        ? `insights/workflows/${workflowName}`
        : `insights/apps/${appName}`;

      const fetchedData = !queryParams
        ? await request.get({ endpoint })
        : await request.get({
            endpoint,
            queryParams,
          });
      setData(fetchedData[0]);
      setFetchRange(range);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshKPIData = useCallback(() => {
    if (currentAppName && fetchRange) {
      fetchKPI(currentAppName, fetchRange);
    } else if (workflowName) {
      fetchKPI(workflowName, fetchRange);
    }
  }, [currentAppName, fetchRange, fetchKPI, workflowName]);

  const refreshSankeyData = useCallback(
    (factName?: string, updatedRange?: DateRange) => {
      const range = updatedRange ? updatedRange : fetchRange;
      if (currentAppName && range) {
        fetchSankeyData(
          currentAppName,
          factName,
          selectedQualityFilter?.value,
          range,
          workflowNames
        );
      }
    },
    [currentAppName, fetchRange, fetchSankeyData, workflowNames]
  );

  const fetchFactsData = useCallback(async (appName: string) => {
    try {
      setResponseError(null);
      setFactsLoading(true);
      const fetchedData: any = await request.get({
        endpoint: `metadata/apps/${appName}/facts`,
      });
      const facts = fetchedData[0].facts;

      const formattedDataInKeyLabelFormat = Object.keys(facts).map((key) => ({
        key,
        label: facts[key],
      }));

      setFactsData(formattedDataInKeyLabelFormat);
    } catch (error: any) {
      setFactsData([]);
      console.error('Error fetching facts data:', error);
      toast.error(error?.data?.error?.message);
    } finally {
      setFactsLoading(false);
    }
  }, []);

  const fetchInsightsFactIds = useCallback(
    async (
      appName: string,
      factName: string,
      workflows?: string | null,
      range?: { from: Date | null; to: Date | null }
    ) => {
      try {
        const queryParams: Record<string, string> = {
          page_size: String(MAX_FACT_IDS),
        };

        if (range?.from) {
          queryParams.start_time = range.from.toISOString();
        }
        if (range?.to) {
          queryParams.end_time = range.to.toISOString();
        }
        if (workflows) {
          queryParams.workflow_names = workflows;
        }

        const response = await request.get({
          endpoint: `apps/${appName}/facts/${factName}/ids`,
          queryParams,
        });

        return response?.[0]?.fact_ids || {};
      } catch (error) {
        console.error('Error fetching insights fact IDs:', error);
        return {};
      }
    },
    []
  );

  useEffect(() => {
    if (currentAppName && factName && fetchRange) {
      fetchMetaData(currentAppName, factName, fetchRange);
    }
  }, [factName, currentAppName, fetchRange]);

  return (
    <KPIContext.Provider
      value={{
        data,
        loading,
        fetchKPI,
        refreshKPIData,
        sankeyData,
        sankeyDataLoading,
        metaData,
        setMetaData,
        viewBy,
        setViewBy,
        fetchSankeyData,
        refreshSankeyData,
        sankeyDataRefreshTime,
        factsData,
        factsLoading,
        fetchFactsData,
        fetchInsightsFactIds,
        categorizedErrors,
        fetchCategorizedErrors,
        fetchRange,
        nodeKey,
        setNodeKey,
        responseError,
        selectedQualityFilter,
        setSelectedQualityFilter,
        fetchMetaData,
        factName,
        setFactName,
        workflowNames,
        setWorkflowNames,
        selectedFactFilter,
        setSelectedFactFilter,
        selectedFactIds,
        setSelectedFactIds,
      }}
    >
      {children}
    </KPIContext.Provider>
  );
};

// Custom hook to use the KPI context
export const useKPIContext = () => {
  const context = useContext(KPIContext);
  return context;
};
